import { useCallback, useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { withRecommendationProvider } from 'libs/searchspring/components/recommendations/withRecommendationsProvider'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } = useSearchResultState({ query, results, isLoading })
  const Recommendations = useCallback(withRecommendationProvider(CarouselResults, 'recommend'), [])

  const itemClass = 'w-grid-7/8 lg:w-full max-w-[280px] lg:max-w-grid-1/4 mr-gutter [_&>div]:w-full'
  const productCardSettings = {
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false
  }
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: false,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    const config = hasResults ? SEARCH_CONFIG.HAS_RESULTS : SEARCH_CONFIG.NO_RESULTS
    return config.suggestions.map((suggestion, index) => (
      <SearchSuggestions
        key={index}
        title={suggestion.title}
        type={suggestion.type}
        query={query}
        defaultSuggestions={suggestion.default}
        isStatic={suggestion.isStatic}
      />
    ))
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="flex flex-col lg:grid lg:grid-cols-main gap-x-gutter lg:gap-y-1.5 max-h-[75vh] bg-secondary lg:px-margin overflow-y-scroll no-scrollbar overscroll-none">
      {/* Main Content Section */}
      <div className="shrink-0 lg:col-[3/-1] lg:row-[1/2] flex flex-col gap-y-1.25 w-full lg:w-full-margin-half h-fit px-margin lg:pl-2 py-1.5 lg:py-2.5 overflow-x-hidden">
        <div className={clsx(
          'flex items-center justify-between gap-x-gutter w-full'
        )}>
          <div className="flex flex-col gap-y-1.5 w-full">
            {(!hasResults && query && !isLoading && showNoResults) && (
              <span className="w-full paragraph-s lg:paragraph-b p-1 bg-system-error-light">No results for "{query}", please try another search</span>
            )}
            <h2 className="h6 font-medium">{title}</h2>
          </div>

          {(hasResults && !redirect) && (
            <SearchPageLink query={query} className="shrink-0 lg-max:!hidden btn btn-primary btn-fill btn-s btn-bold">
              View {results?.pagination?.totalResults} Results
            </SearchPageLink>
          )}
        </div>

        <div className={clsx('w-full', query && 'min-h-[32rem]')}>
          {/* Loading Overlay */}
          <div className={clsx('w-full overflow-hidden', (isCheckingResults || isLoading) ? '' : 'h-0 opacity-0 pointer-events-none')}>
            <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', (hasResults) ? '' : 'hidden')}>
            {isResultsReady && (
              <QuickSearchResults />
            )}
          </div>
          <div className={clsx('w-full', (!hasResults && query && !isLoading && showNoResults) ? '' : 'hidden')} data-dl-section-id="home">
            {isRecommendationsReady && (
              <Recommendations profile="home" limit={4} itemClass={itemClass} productCardSettings={productCardSettings} carouselSettings={carouselSettings} />
            )}
          </div>
          <div className={clsx('w-full', query ? 'hidden' : '')}>
            {popularCategories.current && <PopularCategories content={popularCategories.current} carouselSettings={carouselSettings} />}
          </div>
        </div>

        {/* Mobile "View results" button */}
        {(hasResults && !redirect) && (
          <SearchPageLink query={query} className="lg:!hidden btn btn-primary btn-fill btn-m btn-bold w-full">
            View {results?.pagination?.totalResults} Results
          </SearchPageLink>
        )}
      </div>

      {/* Suggestions Section */}
      <div className="shrink-0 lg:col-[1/3] lg:row-[1/2] flex flex-col gap-y-2.125 w-full lg:w-full-margin-half h-fit lg:h-full px-margin lg:-ml-margin py-1.5 lg:py-2.5 lg:pr-1.75 bg-neutral-light">
        {renderSuggestions()}
      </div>
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content, carouselSettings }) => {
  return (
    <x-carousel
      className="flex w-full h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <x-carousel
      className="flex w-full h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
    >
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </x-carousel>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = true }) => {
  return (
    <div className="flex flex-col gap-y-1.25 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full lg:after:w-[68%] after:h-px after:bg-neutral-medium pb-2.125 last:after:hidden last:pb-0">
      <h3 className="text-l font-medium">{title}</h3>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : (
        query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
        ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
        ))}
    </div>
  )
}
